import React from 'react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { QueryClient } from '@tanstack/react-query';
import type { EventObject } from '../eventTypes';

/**
 * Paired-event DELETE engine extracted from EventListModule (#3277).
 *
 * `handleDeleteEvent` deletes the primary event, then — for hotel and flight
 * events — locates and deletes the PAIRED counterpart (check-in/check-out, or
 * the other flight leg) before invalidating the events query.
 *
 * Extraction notes (byte-for-byte behavior preservation):
 * - It was a NON-memoized plain `async function` invoked only on user action
 *   (delete buttons / confirmation modals / EventEditModal onDelete), so it is
 *   reproduced verbatim here reading the live deps passed via typed options at
 *   call time — no useCallback, no dependency array, no memoization-identity
 *   change. The optional `e?: React.MouseEvent` arg and its `stopPropagation()`
 *   short-circuit are preserved exactly.
 * - `api.objects.deleteObject` and `queryKeys` are imported directly (the same
 *   module-level imports the orchestrator used); the per-render collaborators
 *   (`allEvents`, `token`, `queryClient`) are threaded in.
 * - The pairing rules (hotel: `eventType === 'hotel' && hotelPairId`; flight:
 *   `flightPairId && flightLeg`; paired = same pairId, different id), the
 *   primary-then-pair-then-invalidate ordering, and the try/catch that swallows
 *   failures with a `console.error` are unchanged. Behavior is pinned by
 *   `useEventDeletion.char.test.ts`.
 */
export interface UseEventDeletionOptions {
  allEvents: any[];
  token: string | null;
  queryClient: QueryClient;
}

export function useEventDeletion(opts: UseEventDeletionOptions) {
  const { allEvents, token, queryClient } = opts;

  async function handleDeleteEvent(eventId: string, e?: React.MouseEvent) {
    if (e) {
      e.stopPropagation();
    }

    try {
      // Find the event being deleted to check for paired flight events
      const eventToDelete = allEvents.find((ev: EventObject) => ev.id === eventId);

      // Delete the main event
      await api.objects.deleteObject(token ?? null, 'event', eventId);

      // If it's a hotel with a paired check-in/check-out event, delete that too
      if (eventToDelete?.eventType === 'hotel' && eventToDelete?.hotelPairId) {
        const pairedEvent = allEvents.find(
          (ev: EventObject) => ev.hotelPairId === eventToDelete.hotelPairId && ev.id !== eventId
        );
        if (pairedEvent) {
          console.log('[EventListModule] Deleting paired hotel event:', pairedEvent.id);
          await api.objects.deleteObject(token ?? null, 'event', pairedEvent.id);
        }
      }

      // If this is a flight event with a pair, also delete the paired event
      if (eventToDelete?.flightPairId && eventToDelete?.flightLeg) {
        const pairedEvent = allEvents.find(
          (ev: EventObject) =>
            ev.flightPairId === eventToDelete.flightPairId &&
            ev.id !== eventId
        );
        if (pairedEvent) {
          console.log('[EventListModule] Deleting paired flight event:', pairedEvent.id);
          await api.objects.deleteObject(token ?? null, 'event', pairedEvent.id);
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
    } catch (err) {
      console.error('[EventListModule] Failed to delete event:', err);
    }
  }

  return { handleDeleteEvent };
}
